/**
 * The single way the admin talks to the backend.
 *
 * - Attaches the access token; when it has expired (401) it exchanges the refresh
 *   token ONCE for a new pair and retries. If that fails the session is over:
 *   listeners are told, and the app returns to the login screen with a message.
 * - Refreshing is single-flight: ten requests failing at once trigger one refresh,
 *   not ten (refresh tokens rotate, so a second use would look like theft).
 * - Uploads go through the same path (`apiUpload`), so they get the same handling.
 */

import { ProblemDto, type PlatformRole } from '@drameeka/shared';
import type { z } from 'zod';

type Problem = z.infer<typeof ProblemDto>;

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

// Tokens live in localStorage: short-lived access token, rotating refresh token.
// (httpOnly cookies are the stronger option and a later hardening step.)
const ACCESS_KEY = 'drameeka.admin.access';
const REFRESH_KEY = 'drameeka.admin.refresh';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null): void => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode): the session simply will not survive a reload.
  }
};

export interface SessionUser {
  id: string;
  email: string;
  displayName: string | null;
  role: PlatformRole;
  tenantId: string | null;
  creatorId?: string | null;
}

export interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: SessionUser;
  mustChangePassword: boolean;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(status: number, problem: Problem) {
    super(problem.title || `Request failed with ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Machine-readable branch point, e.g. `password_change_required`. */
  get code(): string | undefined {
    return this.problem.code;
  }
}

// --- session lifecycle -------------------------------------------------------

type SessionEndedReason = 'expired' | 'signed_out';
const endListeners = new Set<(reason: SessionEndedReason) => void>();

/** Called when the session is gone for good, so the UI can return to login. */
export function onSessionEnded(listener: (reason: SessionEndedReason) => void): () => void {
  endListeners.add(listener);
  return () => endListeners.delete(listener);
}

export function hasStoredSession(): boolean {
  return Boolean(read(REFRESH_KEY) || read(ACCESS_KEY));
}

export function storeSession(session: Pick<SessionResponse, 'accessToken' | 'refreshToken'>): void {
  write(ACCESS_KEY, session.accessToken);
  write(REFRESH_KEY, session.refreshToken);
}

function clearSession(): void {
  write(ACCESS_KEY, null);
  write(REFRESH_KEY, null);
}

function endSession(reason: SessionEndedReason): void {
  clearSession();
  endListeners.forEach((listener) => listener(reason));
}

// --- transport ---------------------------------------------------------------

function buildUrl(path: string, query?: Record<string, string | number | boolean | undefined | null>): string {
  const url = new URL(path.startsWith('http') ? path : `${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function toApiError(response: Response): Promise<ApiError> {
  let problem: Problem = {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
  };
  try {
    const validated = ProblemDto.safeParse(await response.json());
    if (validated.success) problem = validated.data;
  } catch {
    // Non-JSON error body (proxy failure, HTML 502): keep the fallback.
  }
  return new ApiError(response.status, problem);
}

let refreshing: Promise<boolean> | null = null;

/** Exchanges the refresh token for a new pair. Resolves false if that is no longer possible. */
function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    const refreshToken = read(REFRESH_KEY);
    if (!refreshToken) return false;
    try {
      const response = await fetch(buildUrl('/admin/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      storeSession((await response.json()) as SessionResponse);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this same promise all see the result.
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
  /** Endpoints that must work without a session (login) skip the auth + refresh handling. */
  anonymous?: boolean;
}

async function send(path: string, init: RequestInit, options: RequestOptions): Promise<Response> {
  const attempt = () => {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    const token = read(ACCESS_KEY);
    if (token && !options.anonymous) headers.set('Authorization', `Bearer ${token}`);
    return fetch(buildUrl(path, options.query), { ...init, headers, signal: options.signal });
  };

  let response = await attempt();
  if (response.status === 401 && !options.anonymous) {
    if (await refreshSession()) {
      response = await attempt();
    }
    if (response.status === 401) {
      endSession('expired');
    }
  }

  // The token says "still on a temporary password" but the account has since chosen
  // one (e.g. the tab was closed right after saving it). A fresh token carries the
  // current truth; if there is none to be had, ask them to sign in again.
  if (response.status === 403 && !options.anonymous && (await isPasswordChangeRequired(response))) {
    if (await refreshSession()) {
      response = await attempt();
    } else {
      endSession('expired');
    }
  }
  return response;
}

async function isPasswordChangeRequired(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as { code?: string };
    return body.code === 'password_change_required';
  } catch {
    return false;
  }
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const init: RequestInit = { method: options.method ?? 'GET' };
  // Only declare a JSON body when there is one: a bodyless POST that claims to be
  // JSON is rejected by the server as an empty body.
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
    init.headers = { 'Content-Type': 'application/json' };
  }
  const response = await send(path, init, options);
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** multipart upload (images, video). The browser sets the boundary, so no Content-Type here. */
export async function apiUpload<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  const response = await send(path, { method: 'POST', body: form }, { signal });
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

// --- auth calls --------------------------------------------------------------

export async function loginRequest(email: string, password: string): Promise<SessionResponse> {
  const session = await api<SessionResponse>('/admin/auth/login', {
    method: 'POST',
    body: { email, password },
    anonymous: true,
  });
  storeSession(session);
  return session;
}

export async function changePasswordRequest(
  currentPassword: string,
  newPassword: string,
): Promise<SessionResponse> {
  const session = await api<SessionResponse>('/admin/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
  storeSession(session);
  return session;
}

export async function fetchMe(): Promise<{ user: SessionUser; mustChangePassword: boolean }> {
  return api('/admin/auth/me');
}

export async function logoutRequest(): Promise<void> {
  const refreshToken = read(REFRESH_KEY);
  clearSession();
  if (refreshToken) {
    // Best effort: the local session is already gone either way.
    await fetch(buildUrl('/admin/auth/logout'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => undefined);
  }
}
